/**
 * Coach Message Component
 * Displays individual chat messages with proper markdown formatting
 */

import { renderMarkdown } from '../../lib/markdown'
import { cn } from '../../lib/utils'
import { ChartMessage } from './ChartMessage'
import { RoutineCard } from './RoutineCard'

interface Message {
  id: string
  role: 'user' | 'assistant' | 'system' | 'tool'
  content: string
  tool_call_id?: string
  tool_calls?: Array<{
    id: string
    type: 'function'
    function: {
      name: string
      arguments: string
    }
  }>
}

interface CoachMessageProps {
  message: Message
}

export const CoachMessage = ({ message }: CoachMessageProps) => {
  const isUser = message.role === 'user'

  // Handle tool messages - check for chart and routine responses
  if (message.role === 'tool') {
    try {
      const toolResult = JSON.parse(message.content)
      if (toolResult.type === 'chart') {
        return (
          <div className="flex w-full justify-start">
            <ChartMessage
              chartConfig={toolResult.chartConfig}
              _summary={toolResult.summary}
              className="w-full"
            />
          </div>
        )
      }
      if (toolResult.type === 'routine') {
        return (
          <div className="flex w-full justify-start">
            <RoutineCard
              routineConfig={toolResult.routineConfig}
              _summary={toolResult.summary}
              className="w-full"
            />
          </div>
        )
      }
    } catch (e) {
      // Not a valid JSON or not a recognized type - ignore tool message
    }
    return null
  }

  // Don't render system messages
  if (message.role === 'system') {
    return null
  }

  // Show loading dots for empty assistant messages (initial streaming state)
  if (!isUser && message.content === '' && !message.tool_calls) {
    return (
      <div className="flex w-full justify-start">
        <div className="inline-block">
          <div className="flex justify-center gap-1 px-4 py-3">
            <div className="bg-primary h-2 w-2 animate-bounce rounded-full [animation-delay:-0.3s]"></div>
            <div className="bg-primary h-2 w-2 animate-bounce rounded-full [animation-delay:-0.15s]"></div>
            <div className="bg-primary h-2 w-2 animate-bounce rounded-full"></div>
          </div>
        </div>
      </div>
    )
  }

  // Don't render assistant messages that only have tool_calls and no content
  // (these are intermediate states that shouldn't be displayed)
  if (!isUser && message.tool_calls && message.content === '') {
    return null
  }

  return (
    <div className={cn('flex w-full', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[80%] rounded-lg px-4',
          isUser
            ? 'rounded-br-sm bg-white/30 py-3 text-white'
            : 'w-full max-w-full bg-transparent px-2'
        )}
      >
        <div className="text-sm break-words">
          {isUser ? (
            // User messages: simple text, no markdown
            <div className="whitespace-pre-wrap">{message.content}</div>
          ) : (
            // AI messages: render markdown
            renderMarkdown(message.content)
          )}
        </div>
      </div>
    </div>
  )
}
